import React, { useContext, useEffect, useState } from 'react'
import Link from 'next/link';
import { motion } from 'framer-motion';
import { CalendarClock, CheckCircle2, ChevronRight, Repeat, Sparkles, Clock } from 'lucide-react';
import { RequestContext } from '../../../../context/RequestContext';
import { openModal } from '../../../../service/modal-service';
import { HELPER_SERVICE_HOURS, helperOneOffRows } from 'js/part-time-helper-cost';
import { BookingCardHeader } from '@/commons/service-hub/BookingCardHeader';
import { useServiceAvailability } from '../../../../utils/useServiceAvailability';

// The helper booking card on the hub and the helper sub-pages. Pick how often,
// then the hours: a tile quick-books straight to date & time with both answers
// filled in (Services skips the question wizard). Prices are the calculator's
// (js/part-time-helper-cost.js); the charged amount comes from the API.
// The frequency values are DomesticServiceDateTime's FrequencySelect values
// and the backend's question 11 answers.
const FREQUENCIES = [
    { value: 'Weekly', label: 'Weekly', Icon: Repeat },
    { value: 'Biweekly', label: 'Fortnightly', Icon: CalendarClock },
    { value: 'Only once', label: 'One-time', Icon: Sparkles },
]

const BENEFITS = ["Same helper every visit", "All fees inclusive", "Legal workers", "Instant booking"]

export const PartTimeHelperBooking = (props) => {
    const helperService = props.helperService
    const elderlyCareService = props.elderlyCareService
    const setRequestForm = props.setRequestForm
    const setIsQuickBook = props.setIsQuickBook;
    const currencyCd = props.currencyCd
    const helperType = props.helperType
    const setIsShowJobScope = props.setIsShowJobScope
    // onBook: the logged-in /home sheet (HelperBookingSheet) swaps itself for the booking form.
    // plain: no hero photo behind the card (the /home sheet), so it stays white on every screen size
    // instead of the see-through dark panel the public pages lay over their photo from sm up
    const { title, subtitle, pills, onBook, plain = false } = props
    const onPhoto = (classes) => (plain ? '' : classes)

    const [ frequency, setFrequency ] = useState('Weekly')
    const { setRequestCriteria, setRequestDescription } = useContext(RequestContext);
    const { isSubTypePaused } = useServiceAvailability()

    const isSpecialService = helperType === "Elderly Care" || helperType === "Cooking Assistance";
    const recurringRates = isSpecialService ? elderlyCareService : helperService;
    // One-off visits have a smaller length discount; elderly care and cooking add $5 an hour on top
    const oneOffRates = helperOneOffRows().map((row) => ({
        ...row,
        rate: (Number(row.rate) + (isSpecialService ? 5 * row.unit : 0)).toFixed(2),
    }))
    const rates = frequency === 'Only once' ? oneOffRates : recurringRates
    const isPaused = isSubTypePaused("Domestic Services", helperType)

    // What a weekly or fortnightly visit saves against the same visit one-time, for the line under the tiles
    const savings = HELPER_SERVICE_HOURS.map((hours, idx) => {
        const oneOff = Number(oneOffRates[idx]?.rate || 0)
        const recurring = Number(recurringRates?.[idx]?.rate || 0)
        return { hours, amount: oneOff - recurring, percent: oneOff > 0 ? (oneOff - recurring) / oneOff * 100 : 0 }
    })
    const bestPercent = Math.floor(Math.max(0, ...savings.map((s) => s.percent)))
    const threeHours = savings.find((s) => s.hours === 3)

    const openRequestForm = () => {
        if (onBook) return onBook()
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    const quickBook = (hours) => {
        setRequestCriteria([
            {0: "Part time helper", criteria: 'Type of service - Part time helper'},
            {1: helperType, criteria: 'Type of helper - ' + helperType},
            {11: frequency, criteria: 'Frequency of service - ' + frequency},
            {3: hours.toString(), criteria: "Length of each service in hours - " + hours}
        ])
        setRequestDescription('')
        setIsQuickBook(true)
        if (onBook) onBook()
        else openModal(setRequestForm)
    }

    useEffect(() => {
        setRequestCriteria([])
        setRequestDescription('')
        setIsQuickBook(false)
    }, [setIsQuickBook, setRequestCriteria, setRequestDescription])

    const header = title
        ? <div className="pb-4">
            <BookingCardHeader
                title={title}
                subtitle={subtitle}
                pills={pills}
                titleClassName="text-xl sm:text-2xl"
                subtitleClassName={`text-sm text-gray-600 ${onPhoto('sm:text-white/90')}`}
            />
        </div>
        // The /home sheet has its own title
        : plain ? null : <p className="text-center font-bold leading-relaxed uppercase pb-2 px-1">
            Part Time {helperType} Service
        </p>

    // Not offered yet (admin console > Service Availability): point to the helper service instead
    if (isPaused) {
        return (
            <div className={`py-6 px-4 bg-white ${onPhoto('sm:bg-black/50 sm:backdrop-blur-sm sm:text-white shadow')} max-w-md sm:max-w-3xl mx-auto rounded-2xl text-center space-y-3`}>
                {header}
                <p className="text-sm">
                    {helperType} visits are coming soon. Our part-time helpers already do general home cleaning, weekly, fortnightly or one-time.
                </p>
                <Link href="/sg/part-time-cleaner-singapore"
                    className="inline-flex items-center gap-1 px-5 py-3 rounded-xl font-semibold text-white bg-linear-to-r from-orange-500 to-amber-500 shadow-lg shadow-orange-200/40">
                    Book a part-time helper
                    <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </Link>
            </div>
        )
    }

    return (
        <div className={`py-4 px-3 bg-white ${onPhoto('sm:bg-black/50 sm:backdrop-blur-sm sm:text-white shadow')} max-w-md sm:max-w-3xl mx-auto rounded-2xl`}>
            <div className="pt-2 space-y-4">
                {header}

                {/* How often */}
                <div role="radiogroup" aria-label="How often" className={`grid grid-cols-3 gap-1 p-1 rounded-xl bg-gray-100 ${onPhoto('sm:bg-white/15')}`}>
                    {FREQUENCIES.map(({ value, label, Icon }) => {
                        const selected = frequency === value
                        return (
                            <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => setFrequency(value)}
                                className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors ${selected
                                    ? 'bg-white text-orange-600 shadow-sm'
                                    : `text-gray-600 ${onPhoto('sm:text-white/90')} hover:text-orange-600`}`}
                            >
                                <Icon className="w-4 h-4" aria-hidden="true" />
                                {label}
                            </button>
                        )
                    })}
                </div>

                {/* How long: each tile books it */}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {HELPER_SERVICE_HOURS.map((hours, idx) => {
                        const rate = Number(rates[idx]?.rate || 0)
                        return (
                            <motion.button
                                key={hours}
                                type="button"
                                onClick={() => quickBook(hours)}
                                whileTap={{ scale: 0.97 }}
                                className="rounded-2xl p-3 text-center text-white bg-linear-to-tr from-orange-600 to-amber-400 shadow-sm hover:shadow-md"
                            >
                                <span className="flex items-center justify-center gap-1 text-sm font-medium">
                                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />{hours} hrs
                                </span>
                                <span className="block text-lg font-bold">${rate.toFixed(2)}</span>
                                <span className="block text-xs text-white/85">${(rate / hours).toFixed(2)}/hr</span>
                            </motion.button>
                        )
                    })}
                    <button
                        type="button"
                        onClick={openRequestForm}
                        className="rounded-2xl p-3 text-center text-white bg-linear-to-tr from-slate-700 to-slate-400 text-sm font-semibold sm:hidden"
                    >
                        More options
                    </button>
                </div>

                {/* Weekly / fortnightly cost less than one-time: say so (the plan's billing terms come at checkout) */}
                <p className={`text-xs text-center text-gray-600 ${onPhoto('sm:text-white/85')} px-2`}>
                    {bestPercent > 0 && threeHours && (
                        <span className={`font-semibold text-orange-600 ${onPhoto('sm:text-amber-300')}`}>
                            {frequency === 'Only once'
                                ? <>Book weekly and save ${threeHours.amount.toFixed(2)} on a 3-hour visit. </>
                                : <>You save up to {bestPercent}% with regular visits, ${threeHours.amount.toFixed(2)} on every 3-hour visit. </>}
                        </span>
                    )}
                    Prices in {currencyCd || 'SGD'}, all-inclusive.
                </p>

                <div className="hidden sm:block text-center">
                    <button type="button" onClick={openRequestForm}
                        className="text-white text-sm py-2.5 px-5 rounded-xl bg-linear-to-tr from-slate-700 to-slate-400">
                        More options
                    </button>
                </div>

                {/* Benefits */}
                <div className="grid grid-cols-2 gap-2 text-sm px-2">
                    {BENEFITS.map((text) => (
                        <div className="flex items-center gap-2" key={text}>
                            <CheckCircle2 className={`w-4 h-4 text-orange-500 ${onPhoto('sm:text-amber-300')} shrink-0`} aria-hidden="true" />
                            <span>{text}</span>
                        </div>
                    ))}
                </div>

                {/* Scope */}
                <div className="text-center pt-2">
                    <button
                        type="button"
                        className={`inline-flex items-center gap-1 text-orange-600 text-sm ${onPhoto('sm:text-white')} font-medium`}
                        onClick={() => openModal(setIsShowJobScope)}
                    >
                        What the helper does
                        <ChevronRight className="w-4 h-4" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </div>
    )
}
